"use client";

import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { PiMapPinBold, PiMapPinLine } from "react-icons/pi";

import { Button } from "@/components/button/button";
import Input from "@/components/input/input";
import Popover from "@/components/popover/popover";
import { PAGES } from "@/configs/pages.config";
import { withAnalyticsHeaders } from "@/lib/api/http";
import { DEFAULT_LOCALE, getLocaleFromPathname } from "@/lib/i18n/locales";
import { withSeoLocalePath } from "@/lib/i18n/seo-paths";
import { isValidSocialUrl, normalizeExternalUrl } from "@/lib/social-url";
import { cn } from "@/lib/utils";

type ItineraryExtractorProps = {
  variant?: "section" | "hero";
};

type LocationItem = {
  name: string;
  category: string;
};

const MOCK_LOCATIONS: LocationItem[] = [
  { name: "Shosenkyo Gorge", category: "Nature" },
  { name: "Hottarakashi Onsen", category: "Hot Spring" },
  { name: "Kofu Station", category: "Transport" },
];

type ItineraryPreviewResponse =
  | {
      status: "ok";
      video?: {
        id: string;
        title?: string | null;
        caption?: string | null;
        duration_sec: number;
        thumbnail_url?: string;
        author: {
          username: string;
          display_name: string;
        };
        view_count?: number;
        like_count?: number;
      };
      preview?: {
        teaser_summary?: string;
        teaser_transcript?: string;
        locations?: LocationItem[];
        locations_count?: number;
      };
      locations?: LocationItem[];
      locations_count?: number;
      total_locations?: number;
    }
  | {
      status: "error";
      message: string;
    };

const buildMapsUrl = (name: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;

const formatCount = (count?: number) => {
  if (!count) return "—";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
};

export default function ItineraryExtractor({ variant = "section" }: ItineraryExtractorProps) {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [reelPreview, setReelPreview] = useState<ItineraryPreviewResponse | null>(null);
  const [totalLocations, setTotalLocations] = useState(0);
  const [showPopover, setShowPopover] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isHero = variant === "hero";
  const apiUrl = process.env.NEXT_PUBLIC_API_ENDPOINT as string;
  const router = useRouter();
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) ?? DEFAULT_LOCALE;
  const signupPath = useMemo(() => withSeoLocalePath(locale, PAGES.SIGN_UP.PATH), [locale]);

  const handleExtract = async () => {
    if (status === "loading") return;
    setErrorMessage("");
    setLocations([]);
    setReelPreview(null);
    setTotalLocations(0);
    setStatus("loading");

    const normalizedUrl = normalizeExternalUrl(url);
    if (!normalizedUrl || !isValidSocialUrl(normalizedUrl, "instagram")) {
      setStatus("error");
      setErrorMessage("Please enter a valid Instagram Reel link.");
      return;
    }

    if (!apiUrl) {
      setStatus("error");
      setErrorMessage("Missing API endpoint configuration. Please try again later.");
      return;
    }

    try {
      const res = await fetch(`${apiUrl}/public/tools/instagram/preview`, {
        method: "POST",
        headers: withAnalyticsHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ url: normalizedUrl }),
      });
      const data = (await res.json()) as ItineraryPreviewResponse;

      if (!res.ok || data.status === "error") {
        const message =
          res.status === 429
            ? "Rate limit exceeded, please sign up to get more."
            : data.status === "error"
              ? data.message
              : "Unable to extract locations right now.";
        setStatus("error");
        setErrorMessage(message);
        return;
      }

      const responseLocations = data.preview?.locations ?? data.locations;
      const previewLocations = responseLocations ?? [];
      const resolvedTotal = data.preview?.locations_count ?? data.locations_count ?? data.total_locations ?? previewLocations.length;
      const limitedLocations = responseLocations ? previewLocations.slice(0, 2) : MOCK_LOCATIONS.slice(0, 2);
      setLocations(limitedLocations);
      setTotalLocations(resolvedTotal ?? limitedLocations.length);
      setReelPreview(data);
      setStatus("ready");
    } catch {
      setStatus("error");
      setErrorMessage("We couldn't reach the extractor. Please try again.");
    }
  };

  const countLabel = useMemo(() => {
    if (totalLocations === 0) return "Found 0 Locations";
    return `Found ${totalLocations} Locations`;
  }, [totalLocations]);

  return (
    <div className={cn("w-full max-w-screen-md mx-auto", isHero && "py-2")}>
      <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Travel Reel URL</h3>
            <p className="text-sm text-gray-600 mt-2">Extract every location mentioned in the video.</p>
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <Input
              value={url}
              onChange={event => setUrl(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  void handleExtract();
                }
              }}
              placeholder="https://www.instagram.com/reel/..."
              aria-label="Travel Reel URL"
              aria-invalid={status === "error" && !!errorMessage}
            />
            <Button className="md:shrink-0" isLoading={status === "loading"} onClick={handleExtract}>
              <PiMapPinBold className="h-4 w-4" />
              Extract Locations
            </Button>
          </div>
          {status === "loading" && <p className="text-sm text-gray-600">Scanning audio for place names...</p>}
          {status === "error" && errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
        </div>
      </div>

      {status === "ready" && (
        <div className="mt-6 rounded-3xl border border-dashed border-gray-300 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4">
            <div className="text-center">
              <p className="text-sm uppercase tracking-widest text-gray-500">Itinerary Receipt</p>
              <p className="mt-2 text-lg font-semibold text-gray-900">{countLabel}</p>
            </div>

            {reelPreview?.status === "ok" && (
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-col gap-4 sm:flex-row">
                  {reelPreview.video?.thumbnail_url && (
                    <Image
                      src={reelPreview.video.thumbnail_url}
                      alt="Travel Reel thumbnail"
                      width={144}
                      height={256}
                      sizes="(min-width: 640px) 144px, 100vw"
                      className="w-full sm:w-36 rounded-xl border border-gray-200 object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm text-gray-600">
                      <div className="flex flex-wrap items-center gap-2">
                        {reelPreview.video?.author?.username && (
                          <span className="font-semibold text-gray-900">@{reelPreview.video.author.username}</span>
                        )}
                        {reelPreview.video?.author?.display_name && <span>{reelPreview.video.author.display_name}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <span>{formatCount(reelPreview.video?.view_count)} views</span>
                        <span>{formatCount(reelPreview.video?.like_count)} likes</span>
                      </div>
                    </div>
                    {reelPreview.preview?.teaser_summary && (
                      <p className="mt-3 text-sm text-gray-700">{reelPreview.preview.teaser_summary}</p>
                    )}
                    {reelPreview.preview?.teaser_transcript && (
                      <p className="mt-3 text-xs text-gray-500">Transcript preview: {reelPreview.preview.teaser_transcript}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {locations.map((location, index) => (
                <div
                  key={`${location.name}-${index}`}
                  className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white border border-gray-200 text-sm font-semibold text-gray-900">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{location.name}</p>
                    <p className="text-xs text-gray-500">{location.category}</p>
                  </div>
                  <a
                    href={buildMapsUrl(location.name)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open ${location.name} in Google Maps`}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-primary-400"
                  >
                    <PiMapPinLine className="h-4 w-4" />
                  </a>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
              Want the full list of locations? Sign up to unlock all spots.
            </div>

            <Popover open={showPopover} onOpenChange={setShowPopover}>
              <Popover.Trigger asChild>
                <Button
                  className="w-full justify-center"
                  size="lg"
                  onMouseEnter={() => setShowPopover(true)}
                  onMouseLeave={() => setShowPopover(false)}
                  onClick={() => router.push(signupPath)}
                >
                  Export to Google Maps (Sign up to unlock)
                </Button>
              </Popover.Trigger>
              <Popover.Content
                side="top"
                className="text-sm text-gray-700"
                onOpenAutoFocus={event => event.preventDefault()}
                onCloseAutoFocus={event => event.preventDefault()}
              >
                Create a free account to unlock all locations and export to Google Maps.
              </Popover.Content>
            </Popover>
          </div>
        </div>
      )}
    </div>
  );
}
